import type { RowStatus } from '../generated/enums.js';

/**
 * Segment response for frontend use
 * Mirrors core ApSegment fields needed by the UI, plus entityName from join.
 */
export interface SegmentResponse {
  segmentId: string;
  entityId: string;
  name: string;
  description?: string | null;
  dataType: string;
  hasPredefinedValues: boolean;
  isMECE: boolean;
  status: RowStatus;
  createdAt: string;
  updatedAt: string;
  entityName?: string;
}

export interface CreateSegmentRequest {
  entityId: string;
  name: string;
  description?: string;
  dataType?: string;
  hasPredefinedValues?: boolean;
  /**
   * Whether this segment follows MECE (Mutually Exclusive, Collectively Exhaustive).
   * Only applicable when hasPredefinedValues is true; otherwise treated as false.
   */
  isMECE?: boolean;
}

export type UpdateSegmentRequest = Partial<Omit<CreateSegmentRequest, 'entityId'>>;

/**
 * Segment value response for frontend use
 * Represents predefined values for segments.
 */
export interface SegmentValueResponse {
  valueId: string;
  segmentId: string;
  value: string;
  description?: string | null;
  sortOrder: number;
  status: RowStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * Enriched segment split with display metadata used in classifications.
 */
export interface EnrichedSegmentSplit {
  segmentId: string;
  segmentValueId: string;
  amount: number;
  percent: number;
  segmentName?: string;
  value?: string;
  label?: string | null;
}

// Query types for segments
export interface FindSegmentsQuery {
  entityId?: string;
  page?: number;
  limit?: number;
}

/** A logical field spanning entity-specific definitions. Exact names avoid fuzzy collisions. */
export interface SegmentFieldGroup {
  reference: SegmentResponse;
  segments: SegmentResponse[];
}

export function groupEquivalentSegments(segments: readonly SegmentResponse[]): SegmentFieldGroup[] {
  const groups = new Map<string, SegmentResponse[]>();
  for (const segment of segments) {
    const key = JSON.stringify([segment.name, segment.dataType, segment.hasPredefinedValues]);
    const members = groups.get(key) ?? [];
    members.push(segment);
    groups.set(key, members);
  }
  return [...groups.values()].map((members) => {
    // Oldest definition keeps the identity stable when another entity adds this field.
    members.sort(
      (a, b) => a.createdAt.localeCompare(b.createdAt) || a.segmentId.localeCompare(b.segmentId),
    );
    return { reference: members[0], segments: members };
  });
}
